<template>
  <AppBody :subtitle="$t('help-center.title')">
    <CardModule untitle class="py-4">
      <v-row>
        <v-col cols="12" md="3">
          <v-list rounded>
            <v-list-group
              v-for="platform in filteredPlatforms"
              v-model="platform.active"
              :key="platform.value"
              :prepend-icon="platform.icon"
              active-class="grey lighten-4"
              no-action
            >
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title
                    v-text="platform.title"
                  ></v-list-item-title>
                </v-list-item-content>
              </template>

              <template v-for="topic in filteredTopics(platform.topics)">
                <v-list-item
                  v-if="!topic.subtopics"
                  @click="setCurrentTopic(topic, platform.user_type)"
                  :key="topic.value"
                  :class="[
                    {
                      'grey lighten-4': topic.value === currentTopic.value,
                    },
                    'pl-8',
                  ]"
                  link
                >
                  <v-list-item-content>
                    <v-list-item-title v-text="topic.title"></v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-list-group
                  v-else
                  :key="topic.title"
                  class="custom-group"
                  sub-group
                  no-action
                >
                  <template v-slot:activator>
                    <v-list-item-content>
                      <v-list-item-title>{{ topic.title }}</v-list-item-title>
                    </v-list-item-content>
                  </template>

                  <v-list-item
                    v-for="subtopic in topic.subtopics"
                    :key="subtopic.value"
                    @click="setCurrentTopic(subtopic, platform.user_type)"
                    :class="[
                      {
                        'grey lighten-4': subtopic.value === currentTopic.value,
                      },
                      'pl-12',
                    ]"
                    link
                  >
                    <v-list-item-title
                      v-text="subtopic.title"
                    ></v-list-item-title>
                  </v-list-item>
                </v-list-group>
              </template>
            </v-list-group>
          </v-list>
        </v-col>

        <v-divider v-if="mdAndUp" vertical></v-divider>

        <v-col cols="12" md="9">
          <v-row class="align-center justify-space-between" no-gutters>
            <h1>{{ currentTopic.title }}</h1>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="isAdmin"
                  v-bind="attrs"
                  v-on="on"
                  @click="openValidatePasswordModal"
                  :disabled="loadingHelpCenterTopics"
                  color="primary"
                  width="35"
                  height="35"
                  class="ml-2"
                  elevation="0"
                  fab
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("help-center.change-content") }}</span>
            </v-tooltip>
          </v-row>

          <v-container class="px-0 pt-6 content-container">
            <div v-if="loadingHelpCenterTopics">{{ $t("common.loading") }}</div>
            <HelpCenterFields
              v-else-if="editContent"
              :topic="currentTopic"
              @save="saveContent"
              @close="editContent = false"
            />
            <div
              v-else
              v-html="
                currentDescription || $t('help-center.no-content-registered')
              "
            ></div>
          </v-container>
        </v-col>
      </v-row>
    </CardModule>

    <ValidatePasswordModal
      v-if="showValidatePasswordModal"
      v-model="showValidatePasswordModal"
      @success="showEditContent"
      @close="showValidatePasswordModal = false"
    />
  </AppBody>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import displayHelpers from "../../mixins/breakpoints.mixins";
import helpCenterMixins from "../../mixins/helpCenter.mixins";
import AppBody from "../../layouts/components/AppBody.vue";
import CardModule from "../../components/cards/CardModule.vue";
import HelpCenterFields from "../../components/helpCenter/HelpCenterFields.vue";
import ValidatePasswordModal from "../../components/helpCenter/ValidatePasswordModal.vue";

export default {
  name: "HelpCenter",
  mixins: [displayHelpers, helpCenterMixins],
  components: {
    AppBody,
    CardModule,
    HelpCenterFields,
    ValidatePasswordModal,
  },
  data() {
    return {
      currentTopic: "",
      editContent: false,
      showValidatePasswordModal: false,
    };
  },
  computed: {
    ...mapGetters([
      "isAdmin",
      "helpCenterTopics",
      "userType",
      "loadingHelpCenterTopics",
      "hasHelpCenterPermission",
    ]),
    filteredPlatforms() {
      switch (this.userType) {
        case "admin":
          return this.platforms;
        case "loja":
          return this.platforms.filter(p => p.value !== "admin_eleven");
        case "afiliado":
          return this.platforms.filter(p => p.value == "admin_afiliado");
        default:
          return this.platforms;
      }
    },
    locale() {
      return this.$i18n.locale;
    },
    currentDescription() {
      switch (this.locale) {
        case "pt_BR":
          return this.currentTopic.contentPt;
        case "en_US":
          return this.currentTopic.contentEn;
        case "es_ES":
          return this.currentTopic.contentEs;
        default:
          return this.currentTopic.contentPt;
      }
    },
  },
  methods: {
    ...mapActions(["fetchHelpCenterTopics"]),
    findTopic(value) {
      return this.helpCenterTopics.find(t => t.assunto === value);
    },
    filteredTopics(topics) {
      switch (this.userType) {
        case "admin":
          return topics;
        case "loja":
          return topics.filter(p => p.user_type !== "admin");
        case "afiliado":
          return topics.filter(p => p.user_type == "afiliado");
        default:
          return topics;
      }
    },
    setCurrentTopic(topic, userType) {
      this.editContent = false;
      this.currentTopic = {
        ...topic,
        contentPt: this.findTopic(topic.value)?.conteudo_pt,
        contentEn: this.findTopic(topic.value)?.conteudo_en,
        contentEs: this.findTopic(topic.value)?.conteudo_es,
        id: this.findTopic(topic.value)?.idcentral_ajuda,
        userType: userType,
      };
    },
    showEditContent() {
      this.editContent = !this.editContent;
    },
    async saveContent(data) {
      await this.fetchHelpCenterTopics();
      this.setCurrentTopic(
        { title: data.title, value: data.subject },
        this.userType
      );
    },
    openValidatePasswordModal() {
      if (localStorage.getItem("edit_help_center")) {
        this.showEditContent();
      } else {
        this.showValidatePasswordModal = true;
      }
    },
  },
  async mounted() {
    this.currentTopic = this.platforms[0].topics[0];
    await this.fetchHelpCenterTopics();
    this.setCurrentTopic(this.currentTopic, this.userType);
  },
  destroyed() {
    this.currentTopic = {};
  },
};
</script>

<style scoped>
.custom-group:deep(.v-list-group__header) {
  flex-direction: row-reverse;
}
.custom-group:deep(.v-list-group__header .v-list-group__header__prepend-icon) {
  margin-right: 0;
}

.content-container:deep(img) {
  max-width: 100%;
}
</style>

<style lang="scss">
$sizes-indent: (
  1: 3em,
  2: 6em,
  3: 9em,
  4: 12em,
  5: 15em,
  6: 18em,
  7: 21em,
  8: 24em,
);

@each $element, $value in $sizes-indent {
  .ql-indent-#{$element}:not(.ql-direction-rtl) {
    padding-left: $value;
  }
}

$sizes-font: (
  "small": 0.75em,
  "large": 1.5em,
  "huge": 2.5em,
);

@each $element, $value in $sizes-font {
  .ql-size-#{$element} {
    font-size: $value;
  }
}
</style>
